import Spinner from "@/components/ui/Spinner";
import useAuthStore, { AgencyMembership } from "@/store/authStore";
import useGlobalStore from "@/store/globalStore";
import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";

/**
 * In-portal agency switcher rendered in the app shell. Lists the user's
 * agencies (loaded from GET /agencies) and on selection calls the store's
 * `switchAgency()` (which delegates to POST /agency/switch and refreshes the
 * auth payload). Hidden for users with a single agency since they have
 * nothing to switch between.
 */
export default function AgencySwitcher() {
  const { agencies, activeAgencyId, loadAgencies, switchAgency, user } =
    useAuthStore(
      useShallow((state) => ({
        agencies: state.agencies,
        activeAgencyId: state.activeAgencyId,
        loadAgencies: state.loadAgencies,
        switchAgency: state.switchAgency,
        user: state.user,
      })),
    );

  const showError = useGlobalStore((state) => state.showError);
  const showSuccess = useGlobalStore((state) => state.showSuccess);

  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Lazily fetch the user's agencies on first mount so the switcher knows
  // whether to render. We only render the dropdown for multi-agency users.
  useEffect(() => {
    let cancelled = false;
    if (!user) return;
    (async () => {
      try {
        await loadAgencies();
      } catch (err) {
        // Failure here is non-fatal — we just won't show the switcher.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, loadAgencies]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Single-agency or zero-agency users don't need a switcher.
  if (!loaded || !user || agencies.length < 2) {
    return null;
  }

  const activeAgency: AgencyMembership | undefined = agencies.find(
    (a) => a.agency_id === activeAgencyId,
  );

  const label = activeAgency?.agency_name || "Select agency";

  const handleSelect = async (agencyId: string) => {
    if (agencyId === activeAgencyId) {
      setOpen(false);
      return;
    }
    setSwitching(agencyId);
    try {
      await switchAgency(agencyId);
      const target = agencies.find((a) => a.agency_id === agencyId);
      showSuccess(
        target?.agency_name
          ? `Switched to ${target.agency_name}.`
          : "Agency switched.",
      );
      // Refetch agency-scoped data on the current page by reloading. This is
      // the simplest way to ensure every cached store/page reflects the new
      // agency without threading a refetch hook through every screen.
      window.location.reload();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to switch agency.";
      showError(message);
    } finally {
      setSwitching(null);
      setOpen(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative px-4 py-3 border-b border-gray-100"
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center justify-between text-gray-700 text-sm px-3 py-2 rounded-lg border border-gray-200 hover:border-pink-300 hover:bg-pink-50 transition-colors"
      >
        <span className="truncate">{label}</span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform flex-shrink-0 ${
            open ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
        >
          {agencies.map((membership) => {
            const isActive = membership.agency_id === activeAgencyId;
            const isSwitching = switching === membership.agency_id;
            return (
              <li key={membership.agency_id}>
                <button
                  type="button"
                  disabled={switching !== null}
                  onClick={() => handleSelect(membership.agency_id)}
                  className={`w-full text-left px-3 py-2 text-sm flex justify-between items-center transition-colors ${
                    isActive
                      ? "bg-pink-50 text-[#EA33C0] font-semibold"
                      : "text-gray-800 hover:bg-pink-50"
                  } ${switching !== null && !isSwitching ? "opacity-60 cursor-wait" : ""}`}
                >
                  <span className="flex flex-col flex-1 min-w-0">
                    <span className="truncate">
                      {membership.agency_name || membership.agency_id}
                    </span>
                    <span className="text-xs text-gray-500 truncate">
                      {membership.role}
                    </span>
                  </span>
                  {isSwitching && <Spinner size={16} />}
                  {!isSwitching && isActive && (
                    <span className="text-xs text-[#EA33C0] ml-2 flex-shrink-0">
                      Active
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
